<script setup>
import ApplicationLogo from '@/components/ApplicationLogo.vue';
import InputError from '@/components/InputError.vue';
import Button from '@/components/ui/button/Button.vue';
import { Form, FormControl, FormField, FormLabel } from '@/components/ui/form';
import FormItem from '@/components/ui/form/FormItem.vue';
import Input from '@/components/ui/input/Input.vue';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import AuthenticateLayout from '@/Layouts/AuthenticateLayout.vue';
import { TR_MOBILE_INPUT_REGEX } from '@/utils/phoneTurkey';
import { Head, Link, useForm } from '@inertiajs/vue3';
import axios from 'axios';
import { ArrowLeft, Eye, EyeOff, FishIcon, Phone, ShieldCheck } from 'lucide-vue-next';
import { onMounted, ref } from 'vue';
import { route } from 'ziggy-js';

const props = defineProps({
    canResetPassword: {
        type: Boolean,
    },
    status: {
        type: String,
    },
    demoLogin: {
        type: Object,
        default: null,
    },
});

const showPassword = ref(false);

onMounted(() => {
    const preset = new URLSearchParams(window.location.search).get('login');
    if (preset) {
        form.email = preset;
    }
});

const form = useForm({
    email: '',
    password: '',
    remember: false,
});

const submit = () => {
    form.post(route('login'), {
        onFinish: () => {
            form.reset('password');
        },
        onError: (errors) => {
            let reason = '';

            // Hata mesajına göre uygun parametreyi seç
            if (errors.email === 'Hesabınız 30 gündür giriş yapmadığınız için bloke edilmiştir.') {
                reason = 'inactive'; // 30 gün giriş yapmayanlar için
            } else if (errors.email === 'Hesabınız 5 kez yanlış giriş yapıldığı için bloke edilmiştir.') {
                reason = 'failed_attempts'; // 5 kez hatalı giriş yapanlar için
            }

            // Parametreyi URL'ye ekleyerek yönlendirme
            if (reason) {
                window.location.href = route('user.block', { reason: reason });
            }
        },
    });
};

const loginAsDemo = (account) => {
    form.email = account.email;
    form.password = props.demoLogin.password;
    submit();
};

const lowerToTurkish = () => {
    if (!form.email.includes('@')) {
        return;
    }
    form.email = form.email.replace(/İ/g, 'i').toLowerCase();
};

// OTP Login State
const showOtpView = ref(false);
const otpSent = ref(false);
const otpForm = ref({
    phone: '',
    code: '',
});
const otpLoading = ref(false);
const otpError = ref('');

const sanitizePhone = () => {
    if (!TR_MOBILE_INPUT_REGEX.test(otpForm.value.phone)) {
        otpForm.value.phone = otpForm.value.phone.slice(0, 18);
    }
};

const sendOtp = async () => {
    if (!otpForm.value.phone || otpForm.value.phone.length < 10) {
        otpError.value = 'Lütfen geçerli bir telefon numarası giriniz.';
        return;
    }
    otpLoading.value = true;
    otpError.value = '';
    try {
        const response = await axios.post(route('otp.send'), { phone: otpForm.value.phone });
        if (response.data.success) {
            otpSent.value = true;
        }
    } catch (err) {
        otpError.value = err.response?.data?.message || 'Kod gönderilemedi.';
    } finally {
        otpLoading.value = false;
    }
};

const verifyOtp = async () => {
    if (!otpForm.value.code || otpForm.value.code.length !== 6) {
        otpError.value = 'Lütfen 6 haneli doğrulama kodunu giriniz.';
        return;
    }
    otpLoading.value = true;
    otpError.value = '';
    try {
        const response = await axios.post(route('otp.verify'), {
            phone: otpForm.value.phone,
            code: otpForm.value.code,
        });
        if (response.data.success) {
            window.location.href = response.data.redirect;
        }
    } catch (err) {
        otpError.value = err.response?.data?.message || 'Geçersiz kod.';
    } finally {
        otpLoading.value = false;
    }
};
</script>

<template>
    <AuthenticateLayout>
        <Head title="Giriş yap" />
        <section v-if="!showOtpView" class="duration-300 animate-in fade-in">
            <div class="flex w-full items-center justify-center">
                <ApplicationLogo />
            </div>
            <div class="flex flex-col space-y-2">
                <h1 class="text-center text-2xl font-semibold tracking-tight text-black lg:text-2xl">Madalyon’a hoşgeldiniz.</h1>
                <p class="text-center text-[13px] text-gray-500">E-posta, T.C. kimlik no veya telefon ve parolanızla giriş yapın.</p>
            </div>
            <Form class="mt-5 space-y-2" @submit="submit">
                <FormField name="name">
                    <FormItem>
                        <FormLabel :required="true">E-posta, T.C. kimlik no veya telefon</FormLabel>
                        <FormControl>
                            <Input type="text" id="email" placeholder="E-posta, T.C. kimlik no veya telefon" v-model="form.email" required autofocus autocomplete="username" @blur="lowerToTurkish" />
                        </FormControl>
                        <InputError class="mt-2" :message="form.errors.email" />
                    </FormItem>
                    <FormItem>
                        <FormLabel :required="true">Parola</FormLabel>
                        <FormControl>
                            <div class="relative">
                                <Input :type="showPassword ? 'text' : 'password'" id="password" placeholder="Parola" v-model="form.password" required autocomplete="current-password" />
                                <span class="absolute right-0 top-0 flex size-10 items-center justify-center">
                                    <button type="button" @click="showPassword = !showPassword" :aria-label="showPassword ? 'Parolayı gizle' : 'Parolayı göster'" class="text-muted-foreground hover:text-primary">
                                        <EyeOff size="18" v-if="showPassword" />
                                        <Eye size="18" v-else />
                                    </button>
                                </span>
                            </div>
                        </FormControl>
                        <InputError class="mt-2" :message="form.errors.password" />
                    </FormItem>
                </FormField>
                <div class="space-y-4 pt-2">
                    <Button class="w-full" variant="action" :class="{ 'opacity-25': form.processing }" :disabled="form.processing"> Giriş Yap </Button>
                    <Link v-if="canResetPassword" :href="route('password.request')" class="mt-5 flex items-center justify-center">
                        <FishIcon />
                        <div class="ms-2 text-sm font-medium">Parolamı Unuttum</div>
                    </Link>

                    <Separator class="my-6" label="YA DA" />

                    <div class="space-y-3">
                        <Button type="button" variant="outline" class="h-11 w-full border-green-200 text-green-700 hover:bg-green-50 hover:text-green-800" @click="showOtpView = true">
                            <Phone class="mr-2 h-4 w-4" />
                            Üye Olmadan Devam Et
                        </Button>

                        <div class="flex flex-col items-center justify-center pt-2">
                            <div class="text-sm font-semibold text-gray-400">Hesabınız yok mu?</div>
                            <Link v-if="canResetPassword" :href="route('register')" class="mt-2 w-full">
                                <Button variant="action" class="w-full">Kayıt Ol</Button>
                            </Link>
                        </div>
                    </div>

                    <div v-if="demoLogin?.accounts?.length" class="pt-2">
                        <p class="mb-2 text-center text-[11px] font-medium uppercase tracking-wide text-gray-400">Demo giriş</p>
                        <div class="grid grid-cols-2 gap-2">
                            <Button
                                v-for="account in demoLogin.accounts"
                                :key="account.email"
                                type="button"
                                variant="outline"
                                size="sm"
                                class="h-9 border-gray-200 text-xs text-gray-700 hover:border-green-200 hover:bg-green-50 hover:text-green-800"
                                :disabled="form.processing"
                                @click="loginAsDemo(account)"
                            >
                                {{ account.role }}
                            </Button>
                        </div>
                    </div>
                </div>
            </Form>
        </section>

        <!-- OTP Login View -->
        <section v-else class="duration-300 animate-in slide-in-from-right-4">
            <div class="mb-8 flex items-center gap-4">
                <Button
                    variant="ghost"
                    size="icon"
                    @click="
                        showOtpView = false;
                        otpSent = false;
                        otpError = '';
                    "
                >
                    <ArrowLeft class="h-5 w-5" />
                </Button>
                <h1 class="text-xl font-bold">Hızlı Giriş</h1>
            </div>

            <div class="space-y-6">
                <div v-if="!otpSent" class="space-y-4">
                    <div class="mb-4 flex flex-col space-y-2 text-center">
                        <div class="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600">
                            <Phone class="h-6 w-6" />
                        </div>
                        <h2 class="text-lg font-semibold text-black">Telefon ile Doğrula</h2>
                        <p class="text-sm text-gray-500">Giriş yapmak için telefon numaranızı giriniz.</p>
                    </div>

                    <div class="space-y-2">
                        <Label>Telefon Numarası</Label>
                        <div class="relative">
                            <Phone class="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <Input type="tel" v-model="otpForm.phone" v-mask="'+90 (5##) ###-####'" @input="sanitizePhone" placeholder="+90 (5xx) xxx-xxxx" class="h-11 pl-10" autofocus />
                        </div>
                    </div>

                    <Button class="h-11 w-full" variant="action" @click="sendOtp" :disabled="otpLoading">
                        <span v-if="otpLoading" class="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                        Kod Gönder
                    </Button>
                </div>

                <div v-else class="space-y-4">
                    <div class="mb-4 flex flex-col space-y-2 text-center">
                        <div class="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                            <ShieldCheck class="h-6 w-6" />
                        </div>
                        <h2 class="text-lg font-semibold text-black">Kodu Onayla</h2>
                        <p class="text-sm text-gray-500">
                            <strong>{{ otpForm.phone }}</strong> numaralı telefona gönderilen 6 haneli kodu giriniz.
                        </p>
                    </div>

                    <div class="space-y-2">
                        <Label>Doğrulama Kodu</Label>
                        <Input type="text" maxlength="6" placeholder="••••••" class="h-14 text-center text-2xl font-bold tracking-[1em]" v-model="otpForm.code" @keyup.enter="verifyOtp" />
                    </div>

                    <Button class="h-11 w-full" variant="action" @click="verifyOtp" :disabled="otpLoading">
                        <span v-if="otpLoading" class="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                        Doğrula ve Giriş Yap
                    </Button>

                    <button
                        @click="
                            otpSent = false;
                            otpForm.code = '';
                        "
                        class="w-full py-2 text-sm text-gray-500 hover:text-green-600"
                    >
                        Numarayı Değiştir
                    </button>
                </div>

                <div v-if="otpError" class="flex items-center gap-2 rounded-md border border-red-100 bg-red-50 p-3 text-sm text-red-600">
                    <div class="h-1.5 w-1.5 rounded-full bg-red-600"></div>
                    {{ otpError }}
                </div>
            </div>
        </section>
    </AuthenticateLayout>
</template>
